import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Page from '../../components/Page';
import client from '../../lib/feathers';

// Unified Campaigns index — merges userBroadcast rows (one-shot bulk
// emails to opted-in users) and outreachCampaign rows (segment-based
// cold winery outreach) into a single reverse-chronological list with
// type badges. Either deep-link still works (/admin/broadcasts and
// /admin/outreach/campaigns) for the older detail views.

const TYPE_LABELS = {
  broadcast: { label: 'Broadcast', cls: 'bg-blue-100 text-blue-800' },
  outreach_campaign: { label: 'Outreach Campaign', cls: 'bg-purple-100 text-purple-800' },
};

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [broadcasts, campaigns] = await Promise.all([
          client.service('admin/user-broadcast').find({ query: { $limit: 50 } }).catch(() => ({ data: [] })),
          client.service('admin/outreach-campaign').find({ query: { $limit: 50 } }).catch(() => ({ data: [] })),
        ]);

        const merged = [
          ...(broadcasts.data || []).map((b) => ({
            id: `bc:${b.id}`,
            kind: 'broadcast',
            when: b.sentAt || b.createdAt,
            subject: b.subject,
            audience: b.eventKey || '—',
            recipientCount: b.recipientCount || 0,
            status: 'sent',
            sentBy: b.sentByUser
              ? `${b.sentByUser.firstName || ''} ${b.sentByUser.lastName || ''}`.trim() || b.sentByUser.email
              : '—',
            href: '/admin/broadcasts',
          })),
          ...(campaigns.data || []).map((c) => ({
            id: `oc:${c.id}`,
            kind: 'outreach_campaign',
            when: c.sentAt || c.scheduledAt || c.createdAt,
            subject: c.subject || c.name,
            audience: c.type || '—',
            recipientCount: (c.stats && (c.stats.sent || c.stats.total)) || 0,
            status: c.status,
            sentBy: '—',
            href: `/admin/outreach/campaigns/${c.id}`,
          })),
        ];

        merged.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

        if (!cancelled) setRows(merged);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load campaigns');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Page title="Campaigns">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Campaigns</h1>
            <p className="text-sm text-gray-600 mt-1">
              All marketing email sends in one place — broadcasts to opted-in users, cold winery outreach,
              and mixed-audience announcements. Every send tracks Delivery / Open / Click / Bounce via Postmark.
            </p>
          </div>
          <Link
            to="/admin/campaigns/new"
            className="inline-flex items-center px-4 py-2 bg-red-700 text-white rounded-md text-sm font-medium hover:bg-red-800"
          >
            Compose New
          </Link>
        </div>

        {loading && <p className="text-gray-500">Loading…</p>}
        {error && !loading && <p className="text-red-700">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="text-gray-500">No campaigns yet.</p>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto bg-white shadow rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">When</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Audience</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subject</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sent by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const type = TYPE_LABELS[row.kind];
                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {row.when ? formatDistanceToNow(new Date(row.when), { addSuffix: true }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${type.cls}`}>
                          {type.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.audience}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <Link to={row.href} className="hover:underline">{row.subject || '—'}</Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{row.recipientCount.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.status}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.sentBy}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Page>
  );
}
